(() => {
  class FoodTransitionCalculator extends HTMLElement {
    constructor() {
      super();

      this.state = {
        species: 'dog',
        meals: 2,
        gi: 'healthy',
        daysWereEdited: false,
      };

      this.foodComplexity = {
        kibble: 1,
        wet: 2,
        cooked: 3,
        raw: 4,
      };

      this.gutBaseDays = {
        healthy: 7,
        sensitive: 10,
        diarrhoea: 14,
        disease: 21,
      };

      this.handleSegmentClick = this.handleSegmentClick.bind(this);
      this.handleBuildClick = this.handleBuildClick.bind(this);
      this.handleRecommendedClick =
        this.handleRecommendedClick.bind(this);
      this.handleDaysInput = this.handleDaysInput.bind(this);
      this.handleFoodChange = this.handleFoodChange.bind(this);
      this.handlePrint = this.handlePrint.bind(this);
      this.handleEdit = this.handleEdit.bind(this);
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.fromSelect = this.querySelector('[data-pftc-from]');
      this.toSelect = this.querySelector('[data-pftc-to]');
      this.oldAmountInput = this.querySelector(
        '[data-pftc-old-amount]'
      );
      this.newAmountInput = this.querySelector(
        '[data-pftc-new-amount]'
      );
      this.daysInput = this.querySelector('[data-pftc-days]');
      this.buildButton = this.querySelector('[data-pftc-build]');
      this.resetDaysButton = this.querySelector(
        '[data-pftc-reset-days]'
      );
      this.results = this.querySelector('[data-pftc-results]');
      this.planTitle = this.querySelector(
        '[data-pftc-plan-title]'
      );
      this.planLead = this.querySelector('[data-pftc-plan-lead]');
      this.summary = this.querySelector('[data-pftc-summary]');
      this.planNote = this.querySelector('[data-pftc-plan-note]');
      this.mealCaption = this.querySelector(
        '[data-pftc-meal-caption]'
      );
      this.scheduleBody = this.querySelector(
        '[data-pftc-schedule-body]'
      );
      this.oldHeading = this.querySelector(
        '[data-pftc-old-heading]'
      );
      this.newHeading = this.querySelector(
        '[data-pftc-new-heading]'
      );
      this.tableHint = this.querySelector(
        '[data-pftc-table-hint]'
      );
      this.probioticText = this.querySelector(
        '[data-pftc-probiotic-text]'
      );
      this.printButton = this.querySelector('[data-pftc-print]');
      this.editButton = this.querySelector('[data-pftc-edit]');

      this.copy = this.readCopy();

      this.bindEvents();
      this.applyRecommendedDays();

      this.dataset.initialized = 'true';
    }

    disconnectedCallback() {
      this.unbindEvents();
    }

    readCopy() {
      const script = this.querySelector('[data-pftc-copy]');

      if (!script) return {};

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        console.error(
          'Food Transition Calculator: invalid copy JSON.',
          error
        );

        return {};
      }
    }

    bindEvents() {
      this.querySelectorAll('[data-pftc-segment]').forEach(
        (group) => {
          group.addEventListener(
            'click',
            this.handleSegmentClick
          );
        }
      );

      this.buildButton?.addEventListener(
        'click',
        this.handleBuildClick
      );

      this.resetDaysButton?.addEventListener(
        'click',
        this.handleRecommendedClick
      );

      this.daysInput?.addEventListener(
        'input',
        this.handleDaysInput
      );

      this.fromSelect?.addEventListener(
        'change',
        this.handleFoodChange
      );

      this.toSelect?.addEventListener(
        'change',
        this.handleFoodChange
      );

      this.printButton?.addEventListener(
        'click',
        this.handlePrint
      );

      this.editButton?.addEventListener(
        'click',
        this.handleEdit
      );
    }

    unbindEvents() {
      this.querySelectorAll('[data-pftc-segment]').forEach(
        (group) => {
          group.removeEventListener(
            'click',
            this.handleSegmentClick
          );
        }
      );

      this.buildButton?.removeEventListener(
        'click',
        this.handleBuildClick
      );

      this.resetDaysButton?.removeEventListener(
        'click',
        this.handleRecommendedClick
      );

      this.daysInput?.removeEventListener(
        'input',
        this.handleDaysInput
      );

      this.fromSelect?.removeEventListener(
        'change',
        this.handleFoodChange
      );

      this.toSelect?.removeEventListener(
        'change',
        this.handleFoodChange
      );

      this.printButton?.removeEventListener(
        'click',
        this.handlePrint
      );

      this.editButton?.removeEventListener(
        'click',
        this.handleEdit
      );
    }

    handleSegmentClick(event) {
      const button = event.target.closest('[data-value]');

      if (!button) return;

      const group = button.closest('[data-pftc-segment]');

      if (!group) return;

      group.querySelectorAll('[data-value]').forEach((item) => {
        const isActive = item === button;

        item.classList.toggle('is-active', isActive);
        item.setAttribute(
          'aria-pressed',
          isActive ? 'true' : 'false'
        );
      });

      const type = group.dataset.pftcSegment;
      const value = button.dataset.value;

      if (type === 'species') {
        this.state.species = value;
      }

      if (type === 'meals') {
        this.state.meals = Number(value);
      }

      if (type === 'gi') {
        this.state.gi = value;
      }

      if (!this.state.daysWereEdited) {
        this.applyRecommendedDays();
      }
    }

    handleBuildClick() {
      const values = this.getValues();
      const errors = this.validate(values);

      if (errors.length) {
        window.alert(errors.join('\n'));
        return;
      }

      const plan = this.buildPlan(values);

      this.renderPlan(plan);

      this.results.hidden = false;

      requestAnimationFrame(() => {
        this.results.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      });
    }

    handleRecommendedClick() {
      this.state.daysWereEdited = false;
      this.applyRecommendedDays();
    }

    handleDaysInput() {
      this.state.daysWereEdited = true;
    }

    handleFoodChange() {
      if (!this.state.daysWereEdited) {
        this.applyRecommendedDays();
      }
    }

    handlePrint() {
      window.print();
    }

    handleEdit() {
      this.results.hidden = true;

      this.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }

    getValues() {
      return {
        species: this.state.species,
        fromFood: this.fromSelect?.value || 'kibble',
        toFood: this.toSelect?.value || 'raw',
        oldAmount: Number(this.oldAmountInput?.value),
        newAmount: Number(this.newAmountInput?.value),
        meals: Number(this.state.meals),
        gi: this.state.gi,
        days: Number(this.daysInput?.value),
      };
    }

    validate(values) {
      const errors = [];

      if (
        !Number.isFinite(values.oldAmount) ||
        values.oldAmount <= 0
      ) {
        errors.push(
          'Enter a valid current daily food amount.'
        );
      }

      if (
        !Number.isFinite(values.newAmount) ||
        values.newAmount <= 0
      ) {
        errors.push(
          'Enter a valid target daily food amount.'
        );
      }

      if (
        !Number.isInteger(values.days) ||
        values.days < 4 ||
        values.days > 45
      ) {
        errors.push(
          'Transition length must be between 4 and 45 days.'
        );
      }

      return errors;
    }

    applyRecommendedDays() {
      if (!this.daysInput) return;

      const values = this.getValues();
      const days = this.getRecommendedDays(values);

      this.daysInput.value = String(days);
    }

    getRecommendedDays(values) {
      let days = this.gutBaseDays[values.gi] || 7;

      const fromComplexity =
        this.foodComplexity[values.fromFood] || 1;

      const toComplexity =
        this.foodComplexity[values.toFood] || 1;

      const difference = Math.abs(
        toComplexity - fromComplexity
      );

      if (difference === 1) {
        days += 2;
      }

      if (difference === 2) {
        days += 4;
      }

      if (difference >= 3) {
        days += 6;
      }

      if (
        values.fromFood === 'raw' ||
        values.toFood === 'raw'
      ) {
        days += 2;
      }

      if (
        values.species === 'cat' &&
        (
          values.fromFood === 'raw' ||
          values.toFood === 'raw'
        )
      ) {
        days += 2;
      }

      return this.clamp(Math.round(days), 4, 45);
    }

    buildPlan(values) {
      const recommendedDays =
        this.getRecommendedDays(values);

      const days = this.clamp(
        Math.round(values.days),
        4,
        45
      );

      const schedule = [];

      for (let day = 1; day <= days; day += 1) {
        const progress =
          days === 1
            ? 1
            : (day - 1) / (days - 1);

        let newPercent = Math.round(progress * 100);

        if (day === 1) {
          newPercent = 10;
        }

        if (day === days) {
          newPercent = 100;
        }

        const oldPercent = 100 - newPercent;

        const oldPerDay =
          values.oldAmount * (oldPercent / 100);

        const newPerDay =
          values.newAmount * (newPercent / 100);

        schedule.push({
          day,
          oldPercent,
          newPercent,
          oldPerDay,
          newPerDay,
          oldPerMeal: oldPerDay / values.meals,
          newPerMeal: newPerDay / values.meals,
        });
      }

      return {
        ...values,
        days,
        recommendedDays,
        schedule,
      };
    }

    renderPlan(plan) {
      const fromLabel = this.getFoodLabel(plan.fromFood);
      const toLabel = this.getFoodLabel(plan.toFood);
      const dailyIncrease = Math.round(100 / plan.days);

      this.renderTitle(
        plan,
        fromLabel,
        toLabel,
        dailyIncrease
      );

      this.renderSummary(
        plan,
        dailyIncrease
      );

      this.renderReason(plan);

      this.renderMealText(plan);

      this.renderHeadings(
        fromLabel,
        toLabel
      );

      this.renderTable(plan);

      this.renderProbiotic(plan);
    }

    renderTitle(
      plan,
      fromLabel,
      toLabel,
      dailyIncrease
    ) {
      const copy = this.copy.plan || {};

      if (this.planTitle) {
        this.planTitle.textContent =
          `${this.capitalize(fromLabel)} → ` +
          `${this.capitalize(toLabel)} ` +
          `${copy.transitionSuffix || 'transition'}`;
      }

      if (this.planLead) {
        this.planLead.textContent =
          `${copy.leadBeforePercent || 'Increase the new food by roughly'} ` +
          `${dailyIncrease}% ` +
          `${copy.leadBeforeDays || 'each day over'} ` +
          `${plan.days} ` +
          `${copy.leadAfterDays || 'days, while dropping the old food by the same amount. Go at your pet’s pace, not the calendar’s.'}`;
      }
    }

    renderSummary(plan, dailyIncrease) {
      if (!this.summary) return;

      const copy = this.copy.summary || {};

      const status =
        plan.days >= plan.recommendedDays
          ? copy.onTrack || 'On track'
          : copy.fasterThanAdvised ||
            'Faster than advised';

      this.summary.innerHTML = `
        ${this.createStat(
          copy.transitionLength || 'Transition length',
          `${plan.days}`,
          copy.days || 'days'
        )}

        ${this.createStat(
          copy.dailyIncrease || 'Daily increase',
          `${dailyIncrease}%`,
          copy.newFoodPerDay || 'new food / day'
        )}

        ${this.createStat(
          copy.recommendedForYou ||
            'Recommended for you',
          `${plan.recommendedDays}`,
          status
        )}
      `;
    }

    createStat(label, value, note) {
      return `
        <div class="food-transition-calculator__stat">
          <span class="food-transition-calculator__stat-label">
            ${this.escapeHtml(label)}
          </span>

          <span class="food-transition-calculator__stat-value">
            ${this.escapeHtml(value)}
          </span>

          <span class="food-transition-calculator__stat-note">
            ${this.escapeHtml(note)}
          </span>
        </div>
      `;
    }

    renderReason(plan) {
      if (!this.planNote) return;

      const reasons = this.copy.reasons || {};

      let text = '';

      const fromComplexity =
        this.foodComplexity[plan.fromFood] || 1;

      const toComplexity =
        this.foodComplexity[plan.toFood] || 1;

      const difference = Math.abs(
        toComplexity - fromComplexity
      );

      if (
        plan.fromFood === 'raw' ||
        plan.toFood === 'raw'
      ) {
        text += reasons.major || '';
      } else if (difference >= 2) {
        text += reasons.format || '';
      } else {
        text += reasons.simple || '';
      }

      if (plan.gi === 'sensitive') {
        text += reasons.sensitive || '';
      }

      if (plan.gi === 'diarrhoea') {
        text += reasons.diarrhoea || '';
      }

      if (plan.gi === 'disease') {
        text += reasons.disease || '';
      }

      if (plan.species === 'cat') {
        text += reasons.cat || '';
      }

      this.planNote.innerHTML = text;
    }

    renderMealText(plan) {
      const mealsCopy = this.copy.meals || {};

      const mealWord =
        plan.meals === 1
          ? mealsCopy.meal || 'meal'
          : mealsCopy.meals || 'meals';

      if (this.mealCaption) {
        this.mealCaption.innerHTML =
          `${mealsCopy.captionBefore || 'Feeding'} ` +
          `<b>${plan.meals} ${this.escapeHtml(mealWord)} ` +
          `${mealsCopy.captionAfter || 'a day.</b> The amounts below are what to put in <b>each meal</b> — feed the same at every meal that day.'}`;
      }

      if (this.tableHint) {
        this.tableHint.innerHTML =
          `${mealsCopy.tableHintBefore || 'Amounts are already split into your'} ` +
          `<b>${plan.meals} ${this.escapeHtml(mealWord)} ` +
          `${mealsCopy.tableHintAfter || 'a day</b>. Weigh with kitchen scales — cups are wildly inconsistent. Mix the old and new food together in the same bowl so it’s one blended meal.'}`;
      }
    }

    renderHeadings(fromLabel, toLabel) {
      const mealsCopy = this.copy.meals || {};
      const perMeal =
        mealsCopy.perMeal || 'per meal';

      if (this.oldHeading) {
        this.oldHeading.textContent =
          `${this.capitalize(fromLabel)} · ${perMeal}`;
      }

      if (this.newHeading) {
        this.newHeading.textContent =
          `${this.capitalize(toLabel)} · ${perMeal}`;
      }
    }

    renderTable(plan) {
      if (!this.scheduleBody) return;

      this.scheduleBody.innerHTML = plan.schedule
        .map((row) => this.renderRow(row, plan))
        .join('');
    }

    renderRow(row, plan) {
      const dayNotes = this.copy.dayNotes || {};
      const isFinal = row.day === plan.days;

      let note =
        dayNotes.default ||
        'Watch appetite & stool. All good → continue.';

      if (row.day === 1) {
        note =
          dayNotes.firstDay ||
          'Start slow. Mix well so it’s one blended meal, not old on one side and new on the other.';
      } else if (isFinal) {
        note =
          dayNotes.fullySwitchedText ||
          'Stay here. Stool should be firm and settled.';
      } else if (
        row.newPercent === 50 ||
        row.day === Math.ceil(plan.days / 2)
      ) {
        note =
          dayNotes.checkIn ||
          'Check-in day: stool firm? Continue. Loose? Hold or step back.';
      } else if (
        (
          plan.gi === 'diarrhoea' ||
          plan.gi === 'disease'
        ) &&
        row.day === 2
      ) {
        note =
          dayNotes.startProbiotic ||
          'Start the probiotic today.';
      }

      return `
        <tr${isFinal ? ' class="is-final"' : ''}>
          <td class="food-transition-calculator__day">
            ${row.day}
          </td>

          <td>
            <div class="food-transition-calculator__mix">
              <span class="food-transition-calculator__mix-bar">
                <span
                  class="food-transition-calculator__mix-old"
                  style="width: ${row.oldPercent}%"
                ></span>

                <span
                  class="food-transition-calculator__mix-new"
                  style="width: ${row.newPercent}%"
                ></span>
              </span>

              <span class="food-transition-calculator__mix-label">
                ${row.oldPercent}% → ${row.newPercent}%
              </span>
            </div>
          </td>

          <td class="food-transition-calculator__amount">
            ${this.formatGrams(row.oldPerMeal)}

            <small>
              ${this.formatGrams(row.oldPerDay)} / day
            </small>
          </td>

          <td class="food-transition-calculator__amount">
            ${this.formatGrams(row.newPerMeal)}

            <small>
              ${this.formatGrams(row.newPerDay)} / day
            </small>
          </td>

          <td>
            ${
              isFinal
                ? `
                  <span class="food-transition-calculator__tag">
                    ${this.escapeHtml(
                      dayNotes.fullySwitchedTag ||
                        'Fully switched 🎉'
                    )}
                  </span>
                `
                : ''
            }

            ${this.escapeHtml(note)}
          </td>
        </tr>
      `;
    }

    renderProbiotic(plan) {
      if (!this.probioticText) return;

      const copy = this.copy.probiotic || {};

      const strongRecommendation =
        plan.gi === 'diarrhoea' ||
        plan.gi === 'disease';

      const lead = strongRecommendation
        ? copy.strongLead || ''
        : copy.normalLead || '';

      const species =
        plan.species === 'cat'
          ? copy.feline || 'feline'
          : copy.canine || 'canine';

      let html = `
        <p>${lead}</p>

        <p>
          ${copy.speciesPrefix || ''}
          ${species}
          ${copy.speciesSuffix || ''}
        </p>
      `;

      if (
        plan.gi === 'sensitive' ||
        plan.gi === 'diarrhoea'
      ) {
        html += `
          <p>${copy.saccharomyces || ''}</p>
          <p>${copy.fibre || ''}</p>
        `;
      }

      if (plan.gi === 'disease') {
        html += `
          <p>${copy.disease || ''}</p>
        `;
      }

      this.probioticText.innerHTML = html;
    }

    getFoodLabel(food) {
      const labels = this.copy.foodLabels || {};

      return labels[food] || food;
    }

    formatGrams(value) {
      const rounded =
        value < 10
          ? Math.round(value * 10) / 10
          : Math.round(value);

      return `${rounded.toLocaleString()} g`;
    }

    capitalize(value) {
      if (!value) return '';

      return value.charAt(0).toUpperCase() + value.slice(1);
    }

    clamp(value, min, max) {
      return Math.min(Math.max(value, min), max);
    }

    escapeHtml(value) {
      return String(value)
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
    }
  }

  if (!customElements.get('s-food-transition-calculator')) {
    customElements.define(
      's-food-transition-calculator',
      FoodTransitionCalculator
    );
  }
})();
